import { useState } from "react";
import { useForm } from "react-hook-form";
import { collection, doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import "./UserForm.css";

export default function UserForm() {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm();
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // GDPR onay kutusunun değerini izleyelim (varsayılan: false)
  const gdprChecked = watch("gdpr_consent", false);

  // Fullscreen API toggle function
  const toggleFullScreen = () => {
    const docEl = document.documentElement;

    if (
      !document.fullscreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      if (docEl.requestFullscreen) {
        docEl.requestFullscreen();
      } else if (docEl.webkitRequestFullscreen) {
        docEl.webkitRequestFullscreen(); // Safari
      } else if (docEl.msRequestFullscreen) {
        docEl.msRequestFullscreen(); // IE/Edge
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen(); // Safari
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen(); // IE/Edge
      }
    }
  };

  const onSubmit = async (data) => {
    const id = uuidv4();
    await setDoc(doc(collection(db, "users"), id), {
      ...data,
      id,
      createdAt: new Date().toISOString(),
      status: "in-progress",
    });
    toast.success("✅ Record created successfully!", {
      position: "top-center",
      autoClose: 2500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "dark",
    });
    reset();
  };

  const openPrivacyModal = (e) => {
    e.preventDefault();
    setShowPrivacyModal(true);
  };

  const closePrivacyModal = () => {
    setShowPrivacyModal(false);
  };

  return (
    <>
      <div className="form-wrapper">
        <button
          hidden
          className="fs-toggle-btn"
          onClick={toggleFullScreen}
          aria-label="Toggle Fullscreen"
        >
          ⛶
        </button>
        <form className="form-container" onSubmit={handleSubmit(onSubmit)}>
          <h2>Moka United NFC Card</h2>

          <input {...register("first_name")} placeholder="First Name" required />
          <input {...register("last_name")} placeholder="Last Name" required />
          <input
            {...register("email")}
            type="email"
            placeholder="Email Address"
            required
          />
          <input {...register("company_name")} placeholder="Company Name" />
          <div className="phone-group">
            <div className="country-code-input-wrapper">
              <input
                {...register("country_code")}
                type="tel"
                className="country-code-input"
                placeholder="+90"
                required
              />
            </div>
            <input
              {...register("phone_number")}
              type="tel"
              placeholder="1234567890"
              required
            />
          </div>
          <input {...register("job_title")} placeholder="Job Title" />
          <input {...register("website_url")} placeholder="Website URL" autoCapitalize="none" />

          {/* GDPR Consent Checkbox */}
          <div className="gdpr-consent">
            <input
              type="checkbox"
              id="gdpr_consent"
              {...register("gdpr_consent", { required: true })}
            />
            <label htmlFor="gdpr_consent">
              I consent to the processing of my personal data in accordance with the{" "}
              <a href="#" onClick={openPrivacyModal}>
                Privacy Policy
              </a>.
            </label>
          </div>
          {errors.gdpr_consent && (
            <p className="gdpr-error">You must accept before submitting.</p>
          )}

          {/* Buton, GDPR onay kutusu işaretlenmediyse devre dışı */}
          <button type="submit" disabled={!gdprChecked}>
            Save
          </button>
        </form>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="modal-overlay" onClick={closePrivacyModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closePrivacyModal}>
              ×
            </button>
            <div className="privacy-policy-text">
              <h2>Privacy Policy</h2>

              <p>
                <strong>Last updated:</strong> June 4, 2025
              </p>

              <h3>1. Introduction</h3>
              <p>
                Moka United (“we”, “us”, “our”) is committed to protecting the privacy
                and personal data of individuals who interact with our services. This
                Privacy Policy explains how we collect, use, disclose, and safeguard your
                information when you participate in our interactive NFC keychain experience
                at trade shows and similar events.
              </p>

              <h3>2. Data Controller</h3>
              <p>
                The data controller responsible for your personal data is:
                <br />
                Moka United
                <br />
                Levent Mah. Meltem Sk. İş Bankası Kuleleri No: 10 Kule: 2
                <br />
                Beşiktaş/İstanbul
                <br />
                Email:{" "}
                <a href="mailto:real@realworksglobal.com">
                  real@realworksglobal.com
                </a>
              </p>

              <h3>3. Personal Data We Collect</h3>
              <p>
                When you fill out our Moka United NFC Card form, we collect the following:
              </p>
              <ul>
                <li>First and last name</li>
                <li>Email address</li>
                <li>Company name</li>
                <li>Country code and phone number</li>
                <li>Job title</li>
                <li>Website URL</li>
              </ul>
              <p>
                We generate a unique identifier (UUID) for each submission and store a
                timestamp indicating when the record was created. We also track a “status”
                field set to “in-progress” until any further processing is completed.
              </p>

              <h3>4. Why We Process Your Data (Purpose & Legal Basis)</h3>
              <p>We collect and process your personal data in order to:</p>
              <ul>
                <li>
                  <strong>Provide the NFC Keychain Experience:</strong> Your information
                  is encoded as a vCard on an NFC keychain that you receive at our booth.
                  This allows you to share your digital contact card easily with other
                  attendees.
                </li>
                <li>
                  <strong>Marketing & Follow-Up:</strong> With your explicit consent, we
                  may send you occasional updates about Moka United products, events, or
                  special offers that may be of interest to you. You can withdraw this
                  consent at any time.
                </li>
                <li>
                  <strong>Compliance & Record-Keeping:</strong> To comply with legal and
                  regulatory requirements (e.g., tax, accounting, GDPR record-keeping) and
                  to maintain an audit trail of interactions at trade events.
                </li>
              </ul>
              <p>
                Our legal basis for processing is:
                <br />
                – <em>Consent</em>: For marketing communications and sending newsletters
                (only if you opt in).<br />
                – <em>Contractual Necessity</em>: To perform the service you requested
                (i.e., issuing the NFC keychain).<br />
                – <em>Legitimate Interests</em>: For compliance, fraud prevention,
                analytics, and improving our interactive experience.
              </p>

              <h3>5. How We Store and Secure Your Data</h3>
              <p>
                All personal data is stored in Google Firebase Firestore under a secured
                account controlled by Moka United. We implement appropriate technical and
                organizational measures to protect your data against unauthorized access,
                alteration, disclosure, or destruction. Access is restricted to authorized
                personnel only.
              </p>
              <p>
                Your data is retained for as long as necessary to fulfill the purposes
                described in this Privacy Policy, or until you request deletion. Typically,
                we keep your data for up to two (2) years after your last interaction,
                unless a longer retention period is required by law.
              </p>

              <h3>6. Sharing & Disclosure</h3>
              <p>
                We do not sell or rent your personal data to third parties. We may share
                data with:
              </p>
              <ul>
                <li>
                  <strong>Service Providers:</strong> Third-party vendors who assist with
                  data hosting, email delivery (e.g., sending receipts or newsletters),
                  analytics, and other operational tasks. Each provider is bound by
                  contractual obligations to protect your information.
                </li>
                <li>
                  <strong>Legal Authorities:</strong> If required by law or to respond to
                  valid legal requests (e.g., court orders).
                </li>
                <li>
                  <strong>Corporate Transactions:</strong> If Moka United is involved in a
                  merger, acquisition, or sale of assets, your information may be
                  transferred.
                </li>
              </ul>

              <h3>7. Your Rights Under GDPR</h3>
              <p>
                Under the General Data Protection Regulation (GDPR), you have the
                following rights regarding your personal data:
              </p>
              <ul>
                <li>
                  <strong>Right of Access:</strong> You can request a copy of the personal
                  data we hold about you.
                </li>
                <li>
                  <strong>Right to Rectification:</strong> You can ask us to correct any
                  inaccurate or incomplete data.
                </li>
                <li>
                  <strong>Right to Erasure (“Right to be Forgotten”):</strong> You can
                  request deletion of your data when it is no longer necessary for the
                  purposes it was collected.
                </li>
                <li>
                  <strong>Right to Restrict Processing:</strong> You can request that we
                  temporarily suspend processing of your data.
                </li>
                <li>
                  <strong>Right to Data Portability:</strong> You can request your data in
                  a structured, commonly used format (e.g., CSV or JSON).
                </li>
                <li>
                  <strong>Right to Object:</strong> You can object to processing based on
                  legitimate interests or direct marketing at any time.
                </li>
                <li>
                  <strong>Right to Withdraw Consent:</strong> Where processing is based on
                  consent (e.g., marketing emails), you can withdraw your consent at any
                  time.
                </li>
              </ul>
              <p>
                To exercise any of these rights, please contact us at{" "}
                <a href="mailto:real@realworksglobal.com">
                  real@realworksglobal.com
                </a>
                . We will respond to your request within one (1) month, as required by
                law. In some cases (e.g., complex requests), we may extend this period by
                two additional months and will inform you of any such extension.
              </p>

              <h3>8. Cookies & Tracking</h3>
              <p>
                Our website (mokaunited.com) may use cookies and similar tracking
                technologies to enhance user experience, perform analytics, and serve
                targeted advertisements. You can manage your cookie preferences via your
                browser settings. For detailed information, please see our{" "}
                <a
                  href="/cookie-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cookie Policy
                </a>
                .
              </p>

              <h3>9. International Data Transfers</h3>
              <p>
                Our servers are located within the European Union (EU). However, some of
                our third-party service providers may process data outside the EU.
                Whenever we transfer your personal data outside the EU, we ensure
                appropriate safeguards, such as Standard Contractual Clauses (SCCs) or
                other authorized mechanisms, are in place to protect your data.
              </p>

              <h3>10. Children’s Privacy</h3>
              <p>
                Our services are not intended for children under the age of 16. We do not
                knowingly collect personal data from minors. If we become aware that we
                have inadvertently collected data from a child under 16, we will promptly
                delete that data.
              </p>

              <h3>11. Changes to This Privacy Policy</h3>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in
                our data practices or legal requirements. We will post the updated date at
                the top of this page. Continued use of our services after changes are
                published constitutes acceptance of the revised policy.
              </p>

              <h3>12. Contact Us</h3>
              <p>
                If you have questions or concerns about this Privacy Policy or our data
                practices, please contact:
              </p>
              <p>
                <strong>Moka United Data Protection Officer</strong>
                <br />
                Email:{" "}
                <a href="mailto:real@realworksglobal.com">
                  real@realworksglobal.com
                </a>
                <br />
                Address: Levent Mah. Meltem Sk. İş Bankası Kuleleri No: 10 Kule: 2
                Beşiktaş/İstanbul
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}